
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { affiliateService, AffiliateInfo, AffiliateStats, Referral, ReferralCode } from '@/services/affiliateService';
import { toast } from 'sonner';

export const useAffiliateInfo = () => {
  return useQuery({
    queryKey: ['affiliateInfo'],
    queryFn: () => affiliateService.getAffiliateInfo(),
  });
};

export const useAffiliateStats = (timeframe: "7d" | "30d" | "ytd" | "all" = "30d") => {
  return useQuery({
    queryKey: ['affiliateStats', timeframe],
    queryFn: () => affiliateService.getAffiliateStats(timeframe),
  });
};

export const useReferrals = () => {
  return useQuery({
    queryKey: ['referrals'],
    queryFn: () => affiliateService.getReferrals(),
  });
};

export const useReferredUsers = (status: 'all' | 'registered' | 'converted' = 'all') => {
  return useQuery({
    queryKey: ['referredUsers', status],
    queryFn: () => affiliateService.getReferredUsers(status),
    retry: 1, // Giảm số lần thử lại để tránh gọi API quá nhiều khi có lỗi
    meta: {
      onError: (error: Error) => {
        console.error('Error fetching referred users:', error);
        toast.error('Không thể tải danh sách người dùng được giới thiệu. Vui lòng thử lại sau.');
      }
    }
  });
};

export const useReferralCodes = () => {
  return useQuery({
    queryKey: ['referralCodes'],
    queryFn: () => affiliateService.getReferralCodes(),
  });
};

export const useCreateReferralCode = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ description, expiresAt }: { description: string, expiresAt?: Date }) => 
      affiliateService.createReferralCode(description, expiresAt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referralCodes'] });
    },
  });
};

export const useApplyForAffiliate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (applicationData: {
      name: string;
      email: string;
      reason: string;
      website?: string;
      social_media?: string;
    }) => affiliateService.applyForAffiliate(applicationData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliateInfo'] });
    },
  });
};
